import { createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '@/redux/store';
import axios from 'axios';
import { axiosInstance } from '@/services/api/utils/axiosInstance';
import { apiPath } from '@/redux/apiPath';
import { extractAxiosErrorMessage } from '@/utils/getErrorMessage/getErrorMessage.utils';
import { USER_PRIVILEGES_UPDATE_ERROR_PREFIX } from './users.constants';

// Type for project privilege operations
export interface ProjectPrivilegePayload {
  privilegeType: 'READ_PROJECT';
  login: string;
}

// Grant project privileges thunk
export const grantProjectPrivileges = createAsyncThunk<
  boolean, // Return type (success flag)
  { projectId: string; privileges: ProjectPrivilegePayload[] }, // Argument type
  { rejectValue: string; state: RootState }
>('users/grantProjectPrivileges', async ({ projectId, privileges }, { rejectWithValue }) => {
  try {
    // Make the API call
    await axiosInstance.patch(apiPath.grantProjectPrivileges(projectId), privileges, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return true; // Return success flag
  } catch (error: unknown) {
    let errorMessage = USER_PRIVILEGES_UPDATE_ERROR_PREFIX;

    if (error instanceof Error) {
      if (axios.isAxiosError(error)) {
        errorMessage += extractAxiosErrorMessage(error);
      } else {
        errorMessage += error.message;
      }
    } else if (typeof error === 'string') {
      errorMessage += error;
    }

    return rejectWithValue(errorMessage);
  }
});

// Revoke project privileges thunk
export const revokeProjectPrivileges = createAsyncThunk<
  boolean, // Return type (success flag)
  { projectId: string; privileges: ProjectPrivilegePayload[] }, // Argument type
  { rejectValue: string; state: RootState }
>('users/revokeProjectPrivileges', async ({ projectId, privileges }, { rejectWithValue }) => {
  try {
    // Make the API call
    await axiosInstance.patch(apiPath.revokeProjectPrivileges(projectId), privileges, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return true; // Return success flag
  } catch (error: unknown) {
    let errorMessage = USER_PRIVILEGES_UPDATE_ERROR_PREFIX;

    if (error instanceof Error) {
      if (axios.isAxiosError(error)) {
        errorMessage += extractAxiosErrorMessage(error);
      } else {
        errorMessage += error.message;
      }
    } else if (typeof error === 'string') {
      errorMessage += error;
    }

    return rejectWithValue(errorMessage);
  }
});
